import type {
  CustomerSummaryRecord,
  ProjectCollectionRecord,
  ProjectDetailRecord,
  ProjectRecord,
  ProjectStatus
} from '@shared/types';

export interface ProjectCardViewModel {
  id: string;
  title: string;
  tags: string[];
  status: ProjectStatus;
  statusLabel: string;
  statusTone: 'primary' | 'info' | 'warning' | 'success' | 'neutral';
  progressPercent: number | null;
  progressTone: 'success' | 'warning' | 'info';
  activityLabel: string;
  deadlineLabel: string;
  customerName: string;
  contactName: string | null;
  launchedLabel: string | null;
}

export interface ProjectSelectionItemViewModel {
  id: string;
  label: string;
  subtitle: string | null;
}

function getCustomer(summaryList: CustomerSummaryRecord[], customerId: string) {
  return summaryList.find((customer) => customer.id === customerId) ?? null;
}

function statusTone(status: ProjectStatus): ProjectCardViewModel['statusTone'] {
  switch (status) {
    case 'active':
      return 'primary';
    case 'in_progress':
      return 'info';
    case 'pending':
      return 'warning';
    case 'complete':
      return 'success';
    default:
      return 'neutral';
  }
}

function progressTone(status: ProjectStatus): ProjectCardViewModel['progressTone'] {
  if (status === 'pending') {
    return 'warning';
  }

  if (status === 'in_progress') {
    return 'info';
  }

  return 'success';
}

function relativeTime(locale: string, value: string): string {
  const diffInMinutes = Math.round((new Date(value).getTime() - Date.now()) / (1000 * 60));

  if (Math.abs(diffInMinutes) < 60) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(diffInMinutes, 'minute');
  }

  const diffInDays = Math.round(diffInMinutes / (60 * 24));
  if (Math.abs(diffInDays) < 7) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(diffInDays, 'day');
  }

  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(Math.round(diffInDays / 7), 'week');
}

function absoluteDate(locale: string, value: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short'
  }).format(new Date(value));
}

type SortDirection = 'asc' | 'desc';

function withDirection(value: number, direction: SortDirection): number {
  return direction === 'asc' ? value : value * -1;
}

function sortProjects(projects: ProjectRecord[], sort: 'recent' | 'deadline' | 'name', direction: SortDirection): ProjectRecord[] {
  const nextProjects = [...projects];

  switch (sort) {
    case 'deadline':
      return nextProjects.sort((left, right) => {
        if (!left.deadlineAt && !right.deadlineAt) {
          return 0;
        }
        if (!left.deadlineAt) {
          return 1;
        }
        if (!right.deadlineAt) {
          return -1;
        }
        return withDirection(new Date(left.deadlineAt).getTime() - new Date(right.deadlineAt).getTime(), direction);
      });
    case 'name':
      return nextProjects.sort((left, right) => withDirection(left.name.localeCompare(right.name), direction));
    default:
      return nextProjects.sort((left, right) => {
        const leftDate = left.lastActivityAt ?? left.updatedAt;
        const rightDate = right.lastActivityAt ?? right.updatedAt;
        return withDirection(new Date(leftDate).getTime() - new Date(rightDate).getTime(), direction);
      });
  }
}

export function selectProjectCards(
  collection: ProjectCollectionRecord,
  options: {
    locale: string;
    statusFilter: 'all' | ProjectStatus;
    customerFilter: 'all' | string;
    sort: 'recent' | 'deadline' | 'name';
    sortDirection?: SortDirection | undefined;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): ProjectCardViewModel[] {
  const filteredProjects = collection.projects.filter((project) => {
    const matchesStatus = options.statusFilter === 'all' || project.status === options.statusFilter;
    const matchesCustomer = options.customerFilter === 'all' || project.customerId === options.customerFilter;
    return matchesStatus && matchesCustomer;
  });

  return sortProjects(filteredProjects, options.sort, options.sortDirection ?? (options.sort === 'name' ? 'asc' : 'desc')).map((project) => {
    const customer = getCustomer(collection.customerSummaries, project.customerId);
    const latestTimestamp = project.lastActivityAt ?? project.updatedAt;

    return {
      id: project.id,
      title: project.name,
      tags: project.tags,
      status: project.status,
      statusLabel: options.t(`enums.projectStatus.${project.status}`),
      statusTone: statusTone(project.status),
      progressPercent: project.progressPercent,
      progressTone: progressTone(project.status),
      activityLabel: options.t('projects.card.updated', { value: relativeTime(options.locale, latestTimestamp) }),
      deadlineLabel: project.deadlineAt
        ? options.t('projects.card.deadline', { date: absoluteDate(options.locale, project.deadlineAt) })
        : options.t('common.noDeadline'),
      customerName: customer?.name ?? '-',
      contactName: customer?.primaryContactName ?? null,
      launchedLabel: project.launchedAt
        ? options.t('projects.card.launched', { date: absoluteDate(options.locale, project.launchedAt) })
        : null
    };
  });
}

export function selectProjectsSummary(collection: ProjectCollectionRecord) {
  const activeCount = collection.projects.filter((project) => project.status !== 'archived').length;
  const archivedCount = collection.projects.filter((project) => project.status === 'archived').length;

  return {
    activeCount,
    archivedCount
  };
}

export function selectCustomerOptions(collection: ProjectCollectionRecord, allCustomersLabel: string) {
  const customerIds = new Set(collection.projects.map((project) => project.customerId));

  return [
    { value: 'all', label: allCustomersLabel },
    ...collection.customerSummaries
      .filter((customer) => customerIds.has(customer.id))
      .map((customer) => ({
      value: customer.id,
      label: customer.name
      }))
  ];
}

export function selectProjectEditorModel(detail: ProjectDetailRecord, t: (key: string) => string) {
  const customer = getCustomer(detail.customerSummaries, detail.project.customerId);

  return {
    title: detail.project.name,
    customerName: customer?.name ?? '-',
    contactName: customer?.primaryContactName ?? '-',
    statusLabel: t(`enums.projectStatus.${detail.project.status}`),
    stageLabel: t(`enums.projectStage.${detail.project.stage}`),
    deploymentLabel: t(`enums.deploymentState.${detail.project.deploymentState}`),
    endpoints: [
      { key: 'project.fields.primaryDomain', icon: 'website' as const, value: detail.project.primaryDomain },
      { key: 'project.fields.localDomain', icon: 'monitor' as const, value: detail.project.localDomain }
    ].filter((endpoint) => Boolean(endpoint.value))
  };
}

export function selectProjectSelectionItems(collection: ProjectCollectionRecord): ProjectSelectionItemViewModel[] {
  return collection.projects.map((project) => {
    const customer = getCustomer(collection.customerSummaries, project.customerId);

    return {
      id: project.id,
      label: project.name,
      subtitle: customer?.name ?? null
    };
  });
}
